import React, { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import PropTypes from "prop-types"
import FocusLock from "react-focus-lock"
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock"
import { GatsbyImage as Image, getImage } from "gatsby-plugin-image"
import SectionHeader from "../components/SectionHeader"
import IconClose from "../icons/IconClose"
import useThemeColor from "../hooks/useThemeColor"

const getPartnerName = ({ name, logo }) =>
  name?.trim() || logo?.altText || "Partner"

const PartnerLogo = ({ logo, name, className }) => {
  const image = getImage(logo?.localFile)
  const alt = logo?.altText || name

  if (image) {
    return (
      <Image
        className={className}
        image={image}
        alt={alt}
        objectFit="contain"
      />
    )
  }

  // SVGs and media without Sharp image data can still use their original URL.
  if (logo?.mediaItemUrl) {
    return (
      <img
        className={className}
        src={logo.mediaItemUrl}
        alt={alt}
        loading="lazy"
      />
    )
  }

  return (
    <span
      className={`${className} our-partners__logo-placeholder`}
      aria-hidden="true"
    >
      {name.charAt(0)}
    </span>
  )
}

const PartnerPopup = ({ partner, onClose }) => {
  const dialogRef = useRef(null)
  const name = getPartnerName(partner)
  const link = partner.link?.trim()
  const hasLink = /^https?:\/\//i.test(link || "")

  useEffect(() => {
    const dialog = dialogRef.current
    const handleKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault()
        onClose()
      }
    }

    disableBodyScroll(dialog, { reserveScrollBarGap: true })
    document.addEventListener("keydown", handleKeyDown)

    return () => {
      enableBodyScroll(dialog)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [onClose])

  return createPortal(
    <div className="partner-popup">
      <button
        className="partner-popup__overlay"
        type="button"
        tabIndex={-1}
        aria-label="Close partner details"
        onClick={onClose}
      />
      <FocusLock returnFocus className="partner-popup__focus-lock">
        <div
          className="partner-popup__dialog"
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={name}
          tabIndex={-1}
        >
          <button
            className="partner-popup__close"
            type="button"
            onClick={onClose}
            aria-label="Close partner details"
          >
            <IconClose />
          </button>
          <div className="partner-popup__layout">
            <PartnerLogo
              logo={partner.logo}
              name={name}
              className="partner-popup__logo"
            />
            <div className="partner-popup__content">
              <h3 className="partner-popup__name">{name}</h3>
              {partner.description ? (
                <div
                  className="partner-popup__description"
                  dangerouslySetInnerHTML={{ __html: partner.description }}
                />
              ) : null}
              {hasLink ? (
                <a
                  className="partner-popup__link"
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Try For Free
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </FocusLock>
    </div>,
    document.body
  )
}

const OurPartners = ({ title, subtitle, partners }) => {
  const element = useRef(null)
  const [selectedPartner, setSelectedPartner] = useState(null)
  const closePopup = useCallback(() => setSelectedPartner(null), [])
  useThemeColor(element, "dark")

  return (
    <section className="our-partners" ref={element}>
      <div className="our-partners__wrapper container container--mobile-small">
        <SectionHeader title={title} subtitle={subtitle} />
        <ul className="our-partners__grid">
          {(partners || []).map((partner, index) =>
            partner ? (
              <li className="our-partners__partner" key={index}>
                <button
                  className="our-partners__partner-button"
                  type="button"
                  aria-haspopup="dialog"
                  onClick={() => setSelectedPartner(partner)}
                >
                  <PartnerLogo
                    logo={partner.logo}
                    name={getPartnerName(partner)}
                    className="our-partners__partner-logo"
                  />
                  <span className="our-partners__partner-name">
                    {getPartnerName(partner)}
                  </span>
                </button>
              </li>
            ) : null
          )}
        </ul>
      </div>
      {selectedPartner ? (
        <PartnerPopup partner={selectedPartner} onClose={closePopup} />
      ) : null}
    </section>
  )
}

const logoType = PropTypes.shape({
  localFile: PropTypes.object,
  mediaItemUrl: PropTypes.string,
  altText: PropTypes.string,
})
const partnerType = PropTypes.shape({
  logo: logoType,
  name: PropTypes.string,
  description: PropTypes.string,
  link: PropTypes.string,
})

PartnerLogo.propTypes = {
  logo: logoType,
  name: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
}
PartnerPopup.propTypes = {
  partner: partnerType.isRequired,
  onClose: PropTypes.func.isRequired,
}
OurPartners.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  partners: PropTypes.arrayOf(partnerType),
}

export default OurPartners
